import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { toCharacterSheet, type CharacterSheet, type CharacterSummary } from './types'

type Schema = components['schemas']

/**
 * Query keys.
 *
 * The sheet key carries `shapeId`, because a sheet is scoped to one shape: the
 * same character at two shapes is two different sets of skills, stress and
 * consequences. Sharing a key between them would show one shape's stress under
 * the other's name.
 */
export const characterKeys = {
  all: ['characters'] as const,
  lists: () => [...characterKeys.all, 'list'] as const,
  list: (page: number, pageSize: number, search: string, includeHidden: boolean) =>
    [...characterKeys.lists(), page, pageSize, search, includeHidden] as const,
  sheets: () => [...characterKeys.all, 'sheet'] as const,
  sheet: (id: number, shapeId: number | null) => [...characterKeys.sheets(), id, shapeId] as const,
}

export interface CharacterListPage {
  items: CharacterSummary[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

/**
 * The caller's roster, paged and optionally filtered by name.
 *
 * `search` goes to the server rather than being applied to the loaded page,
 * because the filter runs *before* paging there: `totalCount` describes the
 * matches, so a filtered roster pages through matches instead of through
 * everything. It is part of the query key for the same reason — two different
 * searches are two different results, not one list viewed two ways.
 *
 * `placeholderData` keeps the previous page on screen while a new search
 * resolves, so typing does not blank the list between keystrokes.
 *
 * `enabled` is for callers that search rather than browse — the portrait composer's
 * reference picker asks for nothing until there is a term worth asking about, and an
 * unfiltered roster fetched on the way to a search is a request nobody wanted.
 *
 * `includeHidden` asks the server for the characters this user has put away. It is off
 * everywhere except the roster screen's own toggle: a picker that offered hidden characters
 * would not be hiding them. It joins the query key because, exactly like `search`, the two
 * answers are two different lists rather than one list viewed two ways.
 */
export function useCharacterList(
  page = 1,
  pageSize = 50,
  search = '',
  enabled = true,
  includeHidden = false,
): UseQueryResult<CharacterListPage> {
  const trimmed = search.trim()

  return useQuery({
    queryKey: characterKeys.list(page, pageSize, trimmed, includeHidden),
    enabled,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const result = await api.get<Schema['CharacterSummaryDtoPagedResult']>('/characters', {
        query: {
          page,
          pageSize,
          ...(trimmed === '' ? {} : { search: trimmed }),
          ...(includeHidden ? { includeHidden: true } : {}),
        },
      })

      return {
        items: result.items ?? [],
        page: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        totalCount: result.totalCount ?? 0,
        totalPages: result.totalPages ?? 0,
      }
    },
  })
}

/**
 * One sheet read, as options rather than as a hook.
 *
 * Split out because the printed sheet needs several at once — a shapeshifter's skills,
 * stress and consequences are scoped to one shape, so printing every shape means one read
 * per shape through `useQueries`, and a hook cannot be called in a loop. Both callers share
 * this, so the two can never disagree about the key or the shape parameter, and the print
 * route reads the sheet already in cache when it was opened from the screen sheet.
 */
export function characterSheetQuery(characterId: number, shapeId: number | null) {
  return queryOptions({
    queryKey: characterKeys.sheet(characterId, shapeId),
    queryFn: async () =>
      toCharacterSheet(
        await api.get<Schema['CharacterSheetDto']>(`/characters/${characterId}`, {
          query: shapeId === null ? undefined : { shapeId },
        }),
      ),
  })
}

/**
 * Loads one character's full sheet.
 *
 * `shapeId` selects the shape scope; null asks the server for its default, and the
 * response says which one it picked.
 */
export function useCharacterSheet(
  characterId: number,
  shapeId: number | null,
): UseQueryResult<CharacterSheet> {
  return useQuery(characterSheetQuery(characterId, shapeId))
}

export interface CreateCharacterInput {
  name: string
  type: Schema['CharacterType']
  classType?: Schema['CharacterClassType']
  totalRefresh?: number
  /**
   * The power level. Sets the skill-point budget and, unless `totalRefresh` says
   * otherwise, the starting refresh — the server applies that rule, not this client.
   * Omitting it leaves the character with no pool and an unreadable budget.
   */
  skillPool?: Schema['SkillPoolOption']
  /** Shape names, for a Shapeshifter. Each gets its own skills and tracks. */
  shapes?: string[]
  /**
   * Which kind of vampire this is — the id of an ability flagged `isVampirism`.
   * Required for a Vampire and rejected as a 400 without it; the server adds Feeding
   * Dependency alongside it, which is what gives the character a Hunger track.
   */
  vampirismAbilityId?: number
}

export function useCreateCharacter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateCharacterInput) =>
      toCharacterSheet(await api.post<Schema['CharacterSheetDto']>('/characters', input)),

    onSuccess: (sheet) => {
      // Creation is the one character mutation that is *not* an envelope patch —
      // it returns a whole new sheet, and it changes a list this cache holds. So
      // here, unlike every section mutation, invalidating is correct.
      void queryClient.invalidateQueries({ queryKey: characterKeys.lists() })
      queryClient.setQueryData(characterKeys.sheet(sheet.characterId, sheet.shapeId), sheet)
    },
  })
}

/**
 * Hides or unhides a character for the signed-in user.
 *
 * The one character write that does **not** answer with an envelope, because nothing about the
 * character changed — only this user's opinion of whether they want to see it. So the cache
 * work is the mirror image of a section mutation: the sheet keeps every section it holds and
 * gains one flag, and the roster lists are invalidated because their *membership* moved.
 *
 * Both sheet cache entries for the character are patched, not just the one the screen asked
 * for: a shapeshifter is cached once per shape, and hidden-ness belongs to the character
 * rather than to a shape, so leaving the other entries stale would show the wrong menu item
 * the moment someone switched shape.
 */
export function useSetCharacterHidden(characterId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (hidden: boolean) => {
      const result = await api.put<Schema['CharacterVisibilityDto']>(
        `/characters/${characterId}/hidden`,
        { hidden },
      )
      return result.isHidden ?? hidden
    },

    onSuccess: (isHidden) => {
      queryClient.setQueriesData<CharacterSheet>({ queryKey: characterKeys.sheets() }, (sheet) =>
        sheet === undefined || sheet.characterId !== characterId ? sheet : { ...sheet, isHidden },
      )

      void queryClient.invalidateQueries({ queryKey: characterKeys.lists() })
    },
  })
}
